import React, { useEffect, useState } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { Venue } from "../types";

// ── Klubin pelipaikat (asetukset) ─────────────────────────────
//
// Nimi ja osoite kirjoitetaan kerran tänne, ja tapahtuman luonnissa
// paikka valitaan listasta. Lista on klubidokumentin kenttä `venues`;
// sääntö sallii klubin ylläpitäjän päivittää klubidokumenttia, joten
// uutta sääntöä ei tarvita.
//
// Puolten nimet ovat erillinen lomake rivillä eivätkä osa lisäystä:
// paikat ovat jo olemassa, ja juuri niille nimet tarvitaan. Ilman tätä
// nimien antaminen vaatisi paikan poistamisen ja lisäämisen uudelleen.

const C = {
  card: "var(--sl-card)",
  navy: "var(--sl-primary)", text: "var(--sl-text)", muted: "var(--sl-muted)", white: "#ffffff",
  border: "var(--sl-border)", soft: "var(--sl-soft)", error: "var(--sl-error)",
};
const F = { heading: "'Rajdhani', sans-serif", body: "'Sora', sans-serif" };

const card: React.CSSProperties = {
  background: C.card, border: `1px solid ${C.border}`,
  borderRadius: 16, padding: 18, marginBottom: 14,
  boxShadow: "0 4px 16px rgba(22,59,104,0.06)",
};
const btn: React.CSSProperties = {
  padding: "10px 18px", borderRadius: 10, border: "none",
  cursor: "pointer", fontWeight: 700, fontSize: 13, fontFamily: F.body,
};
const input: React.CSSProperties = {
  width: "100%", padding: "12px 14px", boxSizing: "border-box",
  background: C.card, border: `1.5px solid ${C.border}`,
  borderRadius: 10, fontSize: 14, color: C.text,
  fontFamily: F.body, outline: "none", marginBottom: 10,
};

interface Props {
  clubId: string;
  lang?: "fi" | "en";
}

const VenueSettings: React.FC<Props> = ({ clubId, lang = "fi" }) => {
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sidesFor, setSidesFor] = useState<string | null>(null);
  const [sideA, setSideA] = useState("");
  const [sideB, setSideB] = useState("");

  useEffect(() => {
    let alive = true;
    getDoc(doc(db, "clubs", clubId)).then(snap => {
      if (!alive) return;
      setVenues(((snap.data()?.venues as Venue[] | undefined) ?? []).filter(v => v?.name));
      setLoaded(true);
    }).catch(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [clubId]);

  async function persist(next: Venue[]) {
    setSaving(true);
    setError(null);
    try {
      await updateDoc(doc(db, "clubs", clubId), { venues: next });
      setVenues(next);
      return true;
    } catch (err) {
      console.error("venues", err);
      setError(lang === "fi"
        ? "Tallennus epäonnistui. Tarkista yhteys ja yritä uudelleen."
        : "Could not save. Check your connection and try again.");
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function add() {
    const n = name.trim();
    const a = address.trim();
    if (!n) return;
    if (venues.some(v => v.name.toLowerCase() === n.toLowerCase())) {
      setError(lang === "fi" ? "Samanniminen paikka on jo listalla." : "A venue with that name already exists.");
      return;
    }
    if (await persist([...venues, { name: n, address: a }])) {
      setName(""); setAddress(""); setAdding(false);
    }
  }

  /**
   * Avaa puolten nimeämisen yhdelle paikalle.
   *
   * @param v pelipaikka.
   */
  function openSides(v: Venue) {
    setSidesFor(v.name);
    setSideA(v.sideA ?? "");
    setSideB(v.sideB ?? "");
    setError(null);
  }

  /**
   * Tallentaa puolten nimet. Tyhjät poistavat nimet, jolloin puolia ei
   * enää näytetä missään — tasaus jatkuu silti.
   *
   * @param v pelipaikka.
   */
  async function saveSides(v: Venue) {
    const a = sideA.trim();
    const b = sideB.trim();
    if ((a && !b) || (b && !a)) {
      setError(lang === "fi"
        ? "Anna molemmat päät tai jätä molemmat tyhjäksi."
        : "Name both ends, or leave both empty.");
      return;
    }
    const next = venues.map(x => x.name === v.name
      ? {...x, sideA: a || undefined, sideB: b || undefined}
      : x);
    if (await persist(next)) setSidesFor(null);
  }

  async function remove(v: Venue) {
    const ok = window.confirm(lang === "fi"
      ? `Poistetaanko ${v.name} listalta? Vanhat tapahtumat säilyttävät paikkansa.`
      : `Remove ${v.name} from the list? Past events keep their venue.`);
    if (!ok) return;
    await persist(venues.filter(x => x.name !== v.name));
  }

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 16 }}>
        📍 {lang === "fi" ? "Pelipaikat" : "Venues"}
      </div>

      <div style={card}>
        <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5, marginBottom: 12 }}>
          {lang === "fi"
            ? "Tapahtumaa luodessa paikka valitaan tästä listasta. Osoite menee kalenterimerkintään ja kutsuun, jolloin puhelin osaa navigoida perille."
            : "When creating an event, the venue is picked from this list. The address goes into the calendar entry and the invite so phones can navigate there."}
        </div>

        {loaded && venues.length === 0 && !adding && (
          <div style={{ fontSize: 13, color: C.muted, marginBottom: 12 }}>
            {lang === "fi" ? "Ei vielä pelipaikkoja." : "No venues yet."}
          </div>
        )}

        {venues.map(v => (
          <div key={v.name} style={{
            display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10,
            padding: "10px 0", borderBottom: `1px solid ${C.border}`,
          }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.text, fontFamily: F.heading }}>{v.name}</div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                {v.address || (lang === "fi" ? "Ei osoitetta" : "No address")}
              </div>
              {sidesFor === v.name ? (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5, marginBottom: 8 }}>
                    {lang === "fi"
                      ? "Jos kentän päät eivät ole samanarvoisia, nimeä ne. Nimet näkyvät otteluissa, ja Americano jakaa päät tasaisesti niin ettei kukaan pelaa jatkuvasti samassa."
                      : "If the two ends of the court are not equal, name them. The names show up in the matches, and Americano spreads the ends evenly so nobody always plays the same one."}
                  </div>
                  <input value={sideA} onChange={e => setSideA(e.target.value)} autoFocus
                    placeholder={lang === "fi" ? "Pää 1, esim. Seinän puoli" : "End 1, e.g. Wall side"} style={input} />
                  <input value={sideB} onChange={e => setSideB(e.target.value)}
                    placeholder={lang === "fi" ? "Pää 2, esim. Ikkunan puoli" : "End 2, e.g. Window side"} style={input} />
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => saveSides(v)} disabled={saving}
                      style={{ ...btn, flex: 1, background: C.navy, color: C.white }}>
                      ✔ {lang === "fi" ? "Tallenna" : "Save"}
                    </button>
                    <button onClick={() => { setSidesFor(null); setError(null); }}
                      style={{ ...btn, flex: 1, background: C.soft, color: C.text }}>
                      {lang === "fi" ? "Peruuta" : "Cancel"}
                    </button>
                  </div>
                </div>
              ) : v.sideA && v.sideB ? (
                <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
                  ⇄ {v.sideA} · {v.sideB}
                </div>
              ) : null}
            </div>
            {sidesFor !== v.name && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <button onClick={() => openSides(v)} disabled={saving}
                  style={{ ...btn, background: C.soft, color: C.text, padding: "8px 12px", whiteSpace: "nowrap", fontSize: 12 }}>
                  ⇄ {v.sideA && v.sideB
                    ? (lang === "fi" ? "Muuta päät" : "Edit ends")
                    : (lang === "fi" ? "Nimeä päät" : "Name ends")}
                </button>
                <button onClick={() => remove(v)} disabled={saving}
                  style={{ ...btn, background: C.soft, color: C.error, padding: "8px 12px", whiteSpace: "nowrap", fontSize: 12 }}>
                  {lang === "fi" ? "Poista" : "Remove"}
                </button>
              </div>
            )}
          </div>
        ))}

        {adding ? (
          <div style={{ marginTop: 12 }}>
            <input value={name} onChange={e => setName(e.target.value)} autoFocus
              placeholder={lang === "fi" ? "Paikan nimi, esim. Pickleball-halli" : "Venue name, e.g. Pickleball Hall"} style={input} />
            <input value={address} onChange={e => setAddress(e.target.value)}
              placeholder={lang === "fi" ? "Katuosoite, postinumero ja kaupunki" : "Street address, postal code and city"} style={input} />
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={add} disabled={saving || !name.trim()}
                style={{ ...btn, flex: 1, background: C.navy, color: C.white, opacity: !name.trim() ? 0.5 : 1 }}>
                ✔ {lang === "fi" ? "Tallenna" : "Save"}
              </button>
              <button onClick={() => { setAdding(false); setError(null); }}
                style={{ ...btn, flex: 1, background: C.soft, color: C.text }}>
                {lang === "fi" ? "Peruuta" : "Cancel"}
              </button>
            </div>
          </div>
        ) : (
          <button onClick={() => setAdding(true)} disabled={!loaded}
            style={{ ...btn, width: "100%", marginTop: 12, background: C.navy, color: C.white }}>
            + {lang === "fi" ? "Lisää pelipaikka" : "Add venue"}
          </button>
        )}

        {error && <div style={{ fontSize: 12, color: C.error, marginTop: 8 }}>{error}</div>}
      </div>
    </div>
  );
};

export default VenueSettings;
